import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import chatService, {
  type Conversation,
  type ChatMessage,
} from "../services/api/chatService";
import callService, { type CallSession } from "../services/api/callService";

const chatApi = chatService;
const callsApi = callService;

interface ChatContextType {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: ChatMessage[];
  currentCall: CallSession | null;
  isLoading: boolean;
  isMessagesLoading: boolean;
  error: string | null;
  pagination: any;

  fetchConversations: (params?: {
    page?: number;
    limit?: number;
  }) => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  fetchMessages: (
    conversationId: string,
    params?: { page?: number; limit?: number; before?: string },
  ) => Promise<void>;
  sendMessage: (
    conversationId: string,
    content: string,
    mentionsBot?: boolean,
  ) => Promise<void>;
  markAsRead: (conversationId: string) => Promise<void>;
  startCall: (
    conversationId: string,
    participantId: string,
    type?: "video" | "audio",
  ) => Promise<CallSession>;
  acceptCall: (sessionId: string) => Promise<void>;
  declineCall: (sessionId: string) => Promise<void>;
  endCall: (sessionId: string) => Promise<void>;
  leaveConversation: (conversationId: string) => Promise<void>;
  clearError: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentCall, setCurrentCall] = useState<CallSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>(null);

  const fetchConversations = useCallback(async (params = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await chatApi.getConversations(params);
      setConversations(result.conversations);
      setPagination(result.pagination);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch conversations",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const selectConversation = useCallback(async (id: string) => {
    setIsMessagesLoading(true);
    setError(null);
    try {
      const conversation = await chatApi.getConversation(id);
      setCurrentConversation(conversation);
      const messagesResult = await chatApi.getMessages(id);
      setMessages(messagesResult.messages);
      setPagination(messagesResult.pagination);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch conversation",
      );
    } finally {
      setIsMessagesLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(
    async (conversationId: string, params = {}) => {
      setIsMessagesLoading(true);
      setError(null);
      try {
        const result = await chatApi.getMessages(conversationId, params);
        setMessages((prev) =>
          params.page && params.page > 1
            ? [...prev, ...result.messages]
            : result.messages,
        );
        setPagination(result.pagination);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch messages",
        );
      } finally {
        setIsMessagesLoading(false);
      }
    },
    [],
  );

  const sendMessage = useCallback(
    async (conversationId: string, content: string, mentionsBot = false) => {
      setError(null);
      try {
        const message = await chatApi.sendMessage(conversationId, {
          content,
          mentionsBot,
        });
        setMessages((prev) => [message, ...prev]);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send message");
      }
    },
    [],
  );

  const markAsRead = useCallback(async (conversationId: string) => {
    try {
      await chatApi.markRead(conversationId);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv,
        ),
      );
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  }, []);

  const startCall = useCallback(
    async (
      conversationId: string,
      participantId: string,
      type: "video" | "audio" = "video",
    ) => {
      setError(null);
      try {
        const call = await callsApi.createCall({
          conversationId,
          participantId,
          type,
        });
        setCurrentCall(call);
        return call;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to start call");
        throw err;
      }
    },
    [],
  );

  const acceptCall = useCallback(async (sessionId: string) => {
    setError(null);
    try {
      await callsApi.updateCallStatus(sessionId, "accept");
      const call = await callsApi.getCall(sessionId);
      setCurrentCall(call);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept call");
    }
  }, []);

  const declineCall = useCallback(async (sessionId: string) => {
    try {
      await callsApi.updateCallStatus(sessionId, "decline");
      setCurrentCall(null);
    } catch (err) {
      console.error("Failed to decline call:", err);
    }
  }, []);

  const endCall = useCallback(async (sessionId: string) => {
    try {
      await callsApi.updateCallStatus(sessionId, "end");
      setCurrentCall(null);
    } catch (err) {
      console.error("Failed to end call:", err);
    }
  }, []);

  const leaveConversation = useCallback(
    async (conversationId: string) => {
      setError(null);
      try {
        await chatApi.leaveConversation(conversationId);
        setConversations((prev) =>
          prev.filter((conv) => conv.id !== conversationId),
        );
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
          setMessages([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to leave conversation",
        );
      }
    },
    [currentConversation],
  );

  const clearError = useCallback(() => setError(null), []);

  return (
    <ChatContext.Provider
      value={{
        conversations,
        currentConversation,
        messages,
        currentCall,
        isLoading,
        isMessagesLoading,
        error,
        pagination,
        fetchConversations,
        selectConversation,
        fetchMessages,
        sendMessage,
        markAsRead,
        startCall,
        acceptCall,
        declineCall,
        endCall,
        leaveConversation,
        clearError,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export default ChatContext;
